/**
 * Parse a JWT and return a normalized profile object compatible with both
 * legacy self-signed tokens (claims nested under `payload.data`) and
 * Okta access tokens (flat claims with custom extensions).
 *
 * Okta tokens are detected by `iss` containing "okta.com". The Okta
 * authorization server is configured to include `empid` and `user_id`
 * as custom claims; `preferred_username` (e.g. "162383@scripps.com")
 * is used as a fallback for empid extraction when those claims are absent.
 *
 * @param {string} token - Raw JWT string
 * @returns {{ raw: object, profile: object } | null}
 */
export function parseJwtPayload(token) {
  if (!token) return null
  try {
    // base64url → base64 before atob
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const raw = JSON.parse(atob(b64))
    const isOkta = typeof raw.iss === 'string' && raw.iss.includes('okta.com')

    if (!isOkta) {
      // Legacy HMAC-SHA256 JWT: all user data lives in the nested `data` object.
      return { raw, profile: { ...(raw.data ?? {}), expires: raw.exp } }
    }

    // Okta RS256 access token: flat claims.
    // `empid` and `user_id` are custom claims added by the Okta auth server policy.
    // Fall back to the numeric prefix of `preferred_username` when they are absent.
    const empidFromUsername = raw.preferred_username?.split('@')[0] ?? null

    return {
      raw,
      profile: {
        empid: raw.empid ?? empidFromUsername,
        user_id: raw.user_id ?? null,
        // Prefer explicit split claims; fall back to empty — the access API
        // returns DB-resolved names and the store overwrites these on load.
        first_name: raw.given_name ?? raw.first_name ?? '',
        last_name: raw.family_name ?? raw.last_name ?? '',
        name: raw.name ?? '',
        email_address: raw.email ?? raw.preferred_username ?? '',
        location: raw.station_id ?? '',
        location_descr: raw.location_descr ?? '',
        expires: raw.exp
      }
    }
  } catch {
    return null
  }
}
